import axios from "axios";
import { handleActions } from "redux-actions";
import Config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Dispatch } from "interfaces/dispatch";
import { ThunkActionCreator } from "interfaces/thunk";
import { getItem, setItem } from "helpers/storage";

// Action Types
const FETCH_CHAIN_PERMISSION_REQUEST = "FETCH_CHAIN_PERMISSION_REQUEST";
const FETCH_CHAIN_PERMISSION_SUCCESS = "FETCH_CHAIN_PERMISSION_SUCCESS";
const FETCH_CHAIN_PERMISSION_FAILED = "FETCH_CHAIN_PERMISSION_FAILED";

const FETCH_CHAIN_ROLE_REQUEST = "FETCH_CHAIN_ROLE_REQUEST";
const FETCH_CHAIN_ROLE_SUCCESS = "FETCH_CHAIN_ROLE_SUCCESS";
const FETCH_CHAIN_ROLE_FAILED = "FETCH_CHAIN_ROLE_FAILED";

export {
  FETCH_CHAIN_PERMISSION_REQUEST,
  FETCH_CHAIN_PERMISSION_SUCCESS,
  FETCH_CHAIN_PERMISSION_FAILED,
  FETCH_CHAIN_ROLE_REQUEST,
  FETCH_CHAIN_ROLE_SUCCESS,
  FETCH_CHAIN_ROLE_FAILED,
};

// Interfaces
export interface ChainRole {
  id: number;
  role: string;
}

export interface ChainPermission {
  user_id: boolean;
  view_pog: boolean;
  comment_pog: boolean;
  certify_pog: boolean;
  viewmy_comment: boolean;
  viewall_comment: boolean;
  accept_comment: boolean;
  reject_comment: boolean;
  set_reset: boolean;
  comment_reset: boolean;
  validate_reset: boolean;
  take_survey: boolean;
  access_DIR: boolean;
  get_DIR_email: boolean;
}

// Thunk to fetch permissions
const fetchLoggedUserChainsPermissions: ThunkActionCreator<Promise<void>> =
  (selectedChainId, roleId) => async (dispatch: Dispatch) => {
    dispatch({ type: FETCH_CHAIN_PERMISSION_REQUEST });
    try {
      const response = await axios.get(
        `${Config.API_SERVICE_URL}/api/logged-user-chains/chain-permissions/${selectedChainId}/${roleId}`,
        getHeaders()
      );

      dispatch({
        type: FETCH_CHAIN_PERMISSION_SUCCESS,
        payload: response.data,
      });
      const chainRole = getItem("chainRole");

      setItem("chainRole", response.data);
    } catch (error) {
      console.error("Error fetching chain permissions:", error);
      dispatch({ type: FETCH_CHAIN_PERMISSION_FAILED });
    }
  };

// Thunk to fetch role and then permissions
const fetchLoggedUserChainsRole: ThunkActionCreator<Promise<void>> =
  (selectedChainId, roleId) => async (dispatch: Dispatch) => {
    dispatch({ type: FETCH_CHAIN_ROLE_REQUEST });

    try {
      const response = await axios.get(
        `${Config.API_SERVICE_URL}/api/logged-user-chains/permissions/${selectedChainId}/${roleId}`,
        getHeaders()
      );
      dispatch({
        type: FETCH_CHAIN_ROLE_SUCCESS,
        payload: response.data,
      });

      setItem("permission", response.data);

      // then fetch permissions
      await dispatch(fetchLoggedUserChainsPermissions(selectedChainId, roleId));
    } catch (error) {
      console.error("Error fetching chain role:", error);
      dispatch({ type: FETCH_CHAIN_ROLE_FAILED });
    }
  };

export { fetchLoggedUserChainsRole, fetchLoggedUserChainsPermissions };

// Reducer Interface
export interface LoggedUserChainsPermissionState {
  error?: boolean;
  loading?: boolean;
  content?: ChainPermission[];
  chainPermissionLoading?: boolean;
  chainPermissionError?: boolean;
  chainRoleError?: boolean;
  chainRoleLoading?: boolean;
  chainPermission?: string; 
  chainRole?: ChainRole[];
}

// Initial State
const initialState: LoggedUserChainsPermissionState = {
  loading: false,
  error: false,
  content: undefined,
  chainPermissionLoading: false,
  chainPermissionError: false,
  chainRoleLoading: false,
  chainRoleError: false,
  chainPermission: undefined,
  chainRole: undefined,
};

// Actions
const actions = {
  [FETCH_CHAIN_PERMISSION_REQUEST]: (state: LoggedUserChainsPermissionState) => ({
    ...state,
    chainPermissionLoading: true,
    chainPermissionError: false,
    chainPermission: undefined,
  }),

  [FETCH_CHAIN_PERMISSION_SUCCESS]: (
    state: LoggedUserChainsPermissionState,
    action: AnyAction
  ): LoggedUserChainsPermissionState => ({
    ...state,
    chainPermissionLoading: false,
    chainPermission: action.payload.role_name, 
  }),

  [FETCH_CHAIN_PERMISSION_FAILED]: (
    state: LoggedUserChainsPermissionState
  ): LoggedUserChainsPermissionState => ({
    ...state,
    chainPermissionLoading: false,
    chainPermissionError: true,
    chainPermission: undefined,
  }),

  [FETCH_CHAIN_ROLE_REQUEST]: (state: LoggedUserChainsPermissionState) => ({
    ...state,
    chainRoleLoading: true,
    chainRoleError: false,
    chainRole: undefined,
  }),

  [FETCH_CHAIN_ROLE_SUCCESS]: (
    state: LoggedUserChainsPermissionState,
    action: AnyAction
  ): LoggedUserChainsPermissionState => ({
    ...state,
    chainRoleLoading: false,
    chainRole: action.payload,
  }),

  [FETCH_CHAIN_ROLE_FAILED]: (
    state: LoggedUserChainsPermissionState
  ): LoggedUserChainsPermissionState => ({
    ...state,
    chainRoleLoading: false,
    chainRoleError: true,
    chainRole: undefined,
  }),
};

// Reducer
export default handleActions<LoggedUserChainsPermissionState, AnyAction>(
  actions,
  initialState
);
